import { useState } from 'react';

import {
  Box,
  Button,
  ButtonGroup,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  Flex,
  Icon,
  IconButton,
  Radio,
  RadioGroup,
  Select,
  SelectItem,
  TextField,
  Typography,
} from '@project44/manifest-ds';
import { useTranslation } from '@project44/translations-utils';
import { useUser } from '@project44/ui-utils';

const arrOfLength = (num: number) => Array.from(Array(num), (e, i) => i + 1);

const TIME_LIST = [
  '12:00 AM',
  '12:30 AM',
  '1:00 AM',
  '1:30 AM',
  '2:00 AM',
  '2:30 AM',
  '3:00 AM',
  '3:30 AM',
  '4:00 AM',
  '4:30 AM',
  '5:00 AM',
  '5:30 AM',
  '6:00 AM',
  '6:30 AM',
  '7:00 AM',
  '7:30 AM',
  '8:00 AM',
  '8:30 AM',
  '9:00 AM',
  '9:30 AM',
  '10:00 AM',
  '10:30 AM',
  '11:00 AM',
  '11:30 AM',
  '12:00 PM',
  '12:30 PM',
  '1:00 PM',
  '1:30 PM',
  '2:00 PM',
  '2:30 PM',
  '3:00 PM',
  '3:30 PM',
  '4:00 PM',
  '4:30 PM',
  '5:00 PM',
  '5:30 PM',
  '6:00 PM',
  '6:30 PM',
  '7:00 PM',
  '7:30 PM',
  '8:00 PM',
  '8:30 PM',
  '9:00 PM',
  '9:30 PM',
  '10:00 PM',
  '10:30 PM',
  '11:00 PM',
  '11:30 PM',
];

const getMax = (unit: string) => {
  switch (unit) {
    case 'day':
      return arrOfLength(31).map(String);
    case 'week':
      return arrOfLength(4).map(String);
    case 'month':
      return arrOfLength(12).map(String);
    default:
      return arrOfLength(31).map(String);
  }
};

interface IDiaglogNotifySettings {
  isOpen: boolean;
  onClose: () => void;
}

function DialogNotifySettings({ isOpen, onClose }: IDiaglogNotifySettings) {
  const { t } = useTranslation();
  const { data: user } = useUser({ skipRedirectOn401: true });
  const [notifyAbout, setNotifyAbout] = useState('showNew');
  const [notifyFrequency, setNotifyFrequency] = useState('1');
  const [notifyDays, setNotifyDays] = useState(['Mon', 'Wed', 'Fri']);
  const [notifyRepeat, setNotifyRepeat] = useState('1');
  const [notifyTime, setNotifyTime] = useState('8:00 AM');
  const [notifyType, setNotifyType] = useState('updated');
  const [notifyUnit, setNotifyUnit] = useState('day');

  const handleDays = (day: string) => {
    if (notifyDays.includes(day)) {
      setNotifyDays(notifyDays.filter((weekDay) => weekDay !== day));
    } else {
      setNotifyDays([...notifyDays, day]);
    }
  };

  return (
    <Dialog
      isOpen={isOpen}
      onClose={onClose}
      css={{ padding: 0, maxWidth: 'unset', width: 512 }}
    >
      <DialogHeader
        css={{ padding: '$medium' }}
        children={t('core.notificationSettings')}
      />
      <DialogContent css={{ padding: '0' }}>
        <Flex
          orientation="vertical"
          gap="medium"
          css={{ bgColor: '$background-secondary', padding: '$medium' }}
        >
          <RadioGroup
            aria-label={t('core.sendNotification')}
            value={notifyType}
            onChange={(value) => setNotifyType(value)}
          >
            <Typography
              variant="subtextBold"
              children={t('core.sendNotification')}
            />
            <Radio value="updated" children={t('core.whenShipmentAdded')} />
            <Radio value="scheduled" children={t('core.atScheduledTime')} />
          </RadioGroup>
          {notifyType === 'scheduled' && (
            <RadioGroup
              aria-label={t('core.getNotifiedAbout')}
              value={notifyAbout}
              onChange={(value) => setNotifyAbout(value)}
            >
              <Typography
                variant="subtextBold"
                children={t('core.getNotifiedAbout')}
              />
              <Radio value="showNew" children={t('core.newShipmentsAdded')} />
              <Radio value="showAll" children={t('core.allShipmentsWithin')} />
            </RadioGroup>
          )}
        </Flex>
        <Flex orientation="vertical" css={{ padding: '$medium' }} gap="medium">
          {notifyType === 'scheduled' && (
            <Box>
              <Typography
                variant="subtextBold"
                children={t('core.recurrence')}
              />
              <Flex align="center">
                <Typography
                  variant="subtext"
                  css={{ flex: 2 }}
                  children={t('core.repeatEvery')}
                />
                <Flex css={{ flex: 6 }} gap="medium">
                  <Select
                    aria-label="Select notification frequency"
                    selectedKey={notifyFrequency}
                    onSelectionChange={(value) =>
                      setNotifyFrequency(value as string)
                    }
                    children={getMax(notifyUnit).map((item) => (
                      <SelectItem key={item} children={item} />
                    ))}
                  />
                  <Select
                    aria-label="Select notification cadence"
                    selectedKey={notifyUnit}
                    onSelectionChange={(value) => {
                      setNotifyUnit(value as string);
                      setNotifyFrequency('1');
                    }}
                  >
                    <SelectItem key="day">Day</SelectItem>
                    <SelectItem key="week">Week</SelectItem>
                    <SelectItem key="month">Month</SelectItem>
                  </Select>
                </Flex>
              </Flex>
            </Box>
          )}
          {notifyType === 'scheduled' && (
            <Flex align="center">
              <Typography
                variant="subtext"
                css={{ flex: 2 }}
                children={t('core.sendAt')}
              />
              <Select
                aria-label="Select time of notification"
                css={{ flex: 6 }}
                selectedKey={notifyTime}
                onSelectionChange={(value) => setNotifyTime(value as string)}
              >
                {TIME_LIST.map((item) => (
                  <SelectItem key={item}>{item}</SelectItem>
                ))}
              </Select>
            </Flex>
          )}
          {notifyType === 'scheduled' && notifyUnit === 'week' && (
            <Flex align="center">
              <Typography
                variant="subtext"
                css={{ flex: 1 }}
                children={t('core.repeatOn')}
              />
              <Flex css={{ flex: 3 }} gap="small">
                {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(
                  (day) => (
                    <IconButton
                      key={day}
                      aria-label={day}
                      children={day}
                      onPress={() => handleDays(day)}
                      variant="secondary"
                      css={{
                        flexGrow: 1,
                        flexBasis: 1,
                        bgColor: notifyDays.includes(day)
                          ? 'white'
                          : '$background-secondary',
                      }}
                    />
                  ),
                )}
              </Flex>
            </Flex>
          )}
          {notifyType === 'scheduled' && notifyUnit === 'month' && (
            <Flex align="center">
              <Typography
                variant="subtext"
                css={{ flex: 1 }}
                children={t('core.repeatOn')}
              />
              <Flex css={{ flex: 3 }} gap="small">
                <Select
                  aria-label={'Select day of the month'}
                  selectedKey={notifyRepeat}
                  onSelectionChange={(value) =>
                    setNotifyRepeat(value as string)
                  }
                  children={arrOfLength(31).map((item) => (
                    <SelectItem key={item}>{`${item} day`}</SelectItem>
                  ))}
                />
              </Flex>
            </Flex>
          )}
          <TextField
            as="input"
            aria-label={t('core.yourEmailAddress')}
            label={t('core.yourEmailAddress')}
            css={{ pointerEvents: 'none' }}
            value={user?.username || ''}
            readOnly
            isDisabled
            endIcon={<Icon icon="lock" />}
          />
        </Flex>
      </DialogContent>
      <DialogFooter
        css={{ padding: '$medium' }}
        buttonGroupProps={{ css: { flex: 1, justifyContent: 'space-between' } }}
      >
        <Button
          aria-label={t('core.turnOffNotification')}
          children={t('core.turnOffNotification')}
          onPress={() => {}}
          variant="danger"
        />
        <ButtonGroup>
          <Button
            aria-label={t('global.cancel')}
            children={t('global.cancel')}
            onPress={onClose}
            variant="secondary"
          />
          <Button
            aria-label={t('core.saveSettings')}
            children={t('core.saveSettings')}
            onPress={() => {}}
          />
        </ButtonGroup>
      </DialogFooter>
    </Dialog>
  );
}

export default DialogNotifySettings;
